/**
 * Lazily loads the JotForm embed stack. The scripts total ~500 KB, so they
 * are only fetched when a JotForm-enhanced form is about to be used (e.g.
 * scrolled into view) instead of on every page load. Scripts are loaded
 * sequentially so `JotForm` is guaranteed to exist before its plugins and
 * before any `JotForm.*` initialization runs — previously the inline init
 * scripts raced the async script tags and threw
 * "ReferenceError: JotForm is not defined".
 */

const JOTFORM_SCRIPTS = [
  "/scripts/jotform.min.js",
  "https://cdn02.jotfor.ms/static/prototype.forms.js?3.3.43075",
  "https://cdn03.jotfor.ms/static/jotform.forms.js?3.3.43075",
  "https://cdnjs.cloudflare.com/ajax/libs/punycode/1.4.1/punycode.js",
  "https://cdn01.jotfor.ms/js/payments/validategateways.js?v=3.3.43075",
  "https://cdn.jotfor.ms/js/errorNavigation.js?v=3.3.43075",
];

let jotformPromise: Promise<any> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Resolves with the global `JotForm` object once the whole stack is loaded.
 * Subsequent calls return the same promise.
 */
export function loadJotForm(): Promise<any> {
  if (!jotformPromise) {
    jotformPromise = (async () => {
      for (const src of JOTFORM_SCRIPTS) {
        await loadScript(src);
      }
      return (window as any).JotForm;
    })();
  }
  return jotformPromise;
}
